import { useEffect, useState } from 'react';
import { useLocation } from '@gatsbyjs/reach-router';
import { useSelectorMachineContext } from '@printdeal/product-selector';
import { useConvertProduct } from './query/useConvertProduct';
import { Preview } from '../types/design';
import { ServiceFactory } from '../services/ServiceFactory';
import { defaultDocumentReferenceUrl } from '../components/product/detail/helpers/ProductGalleryHelper';

const artworkService = ServiceFactory.createArtworkService();

type STATES = 'loading' | 'loaded';

interface UsePreviewsResponse {
  status: STATES,
  previews: Preview[]
}

/**
 * This hook will return a list of scene previews based on the current selection.
 *
 * @warning This hook only works within the 'SelectorMachineContext'.
 */
export const useScenePreviews = (
  productKey?: string,
  documentReferenceUrl?: string | null,
): UsePreviewsResponse => {
  const location = useLocation();
  const selectorMachineState = useSelectorMachineContext((state) => state);
  const currentSelection = useSelectorMachineContext((state) => state.context.selection);
  const groupSelection = useSelectorMachineContext((state) => state.context.group);
  const invalidSelection = useSelectorMachineContext((state) => state.context.invalidSelection);
  const isPimProduct = useSelectorMachineContext((state) => state.context.product?.isPimProduct);

  const [previews, setPreviews] = useState<Preview[]>([]);
  const [status, setStatus] = useState<STATES>('loading');

  const hasValidSelection = !Object.keys(invalidSelection).length && Object.keys(currentSelection).length > 0;

  const hasValidState = !(selectorMachineState.matches({ STARTED: 'ERRORING' })
    || selectorMachineState.matches({ STARTED: 'VALIDATING' }));

  const { product: mcpProduct } = useConvertProduct(
    { ...currentSelection, ...groupSelection?.[0]?.attributes },
    hasValidSelection && hasValidState && !isPimProduct,
    productKey,
    {
      'x-filename': 'useScenePreviews.ts',
      'x-href': location.href,
    },
  );

  useEffect(() => {
    if (mcpProduct) {
      setStatus('loading');
      const fetchPreviews = async () => {
        try {
          const retrievedPreviews = await artworkService.getPreviews({
            mcpSku: mcpProduct.mcpSku,
            mcpAttributes: JSON.stringify(mcpProduct.attributes),
            documentReferenceUrl: documentReferenceUrl || defaultDocumentReferenceUrl,
            width: 600,
            numberOfCanvasses: 0,
          });
          setPreviews(retrievedPreviews);
          setStatus('loaded');
        } catch (error) {
          // No scenes to show
          setStatus('loaded');
        }
      };
      fetchPreviews();
    }
    setStatus('loaded');
  }, [JSON.stringify(mcpProduct)]);

  return {
    status,
    previews,
  };
};
